import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs';
import { MatchDetailMarketGroupComponent } from './match-detail-market-group.component';
import { MatchDetailHeroComponent } from './match-detail-hero.component';
import { ScrollMemoryService } from '../scroll-memory.service';
import { MatchDetailSectionViewModel, MatchDetailTabId, MatchDetailTabViewModel } from '../sportsbook/sportsbook.models';
import { SportsbookFacadeService } from '../sportsbook/sportsbook-facade.service';
import { SessionService } from '../session.service';
import { MatchDataService } from '../match-data.service';

@Component({
  selector: 'app-match-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatchDetailMarketGroupComponent, MatchDetailHeroComponent],
  templateUrl: './match-detail.component.html',
  styleUrl: './match-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchDetailComponent {
  readonly sportsbook = inject(SportsbookFacadeService);
  readonly session = inject(SessionService);
  private readonly matchData = inject(MatchDataService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly scrollMemory = inject(ScrollMemoryService);
  private readonly matchId = toSignal(
    this.route.paramMap.pipe(map(params => Number(params.get('matchId')))),
    { initialValue: 0 }
  );
  readonly activeTab = signal<MatchDetailTabId>('top');
  readonly isMatchDataLoading = computed(() => {
    const matchId = this.matchId();
    const hasReducedMatch = this.session.matches().has(matchId);

    return this.session.matchesLoading()
      || this.matchData.isMatchDetailsLoading(matchId)
      || (hasReducedMatch
        && !this.matchData.hasMatchDetails(matchId)
        && !this.matchData.hasMatchDetailsFailed(matchId));
  });
  readonly matchDetail = computed(() => {
    const matchId = this.matchId();
    return this.matchData.hasMatchDetails(matchId) ? this.sportsbook.getMatchDetail(matchId) : null;
  });
  readonly currentTabId = computed<MatchDetailTabId | null>(() => {
    const detail = this.matchDetail();
    if (!detail?.tabs.length) {
      return null;
    }

    return detail.tabs.some(tab => tab.id === this.activeTab()) ? this.activeTab() : detail.tabs[0].id;
  });
  readonly visibleSections = computed(() => {
    const detail = this.matchDetail();
    if (!detail) {
      return [];
    }

    const activeTab = this.currentTabId();
    if (!activeTab) {
      return detail.sections;
    }

    return detail.sections.filter(section => section.tabIds.includes(activeTab));
  });

  constructor() {
    effect(() => {
      const matchId = this.matchId();

      this.activeTab.set('top');

      if (matchId > 0) {
        this.scrollMemory.scrollToTop();
      }
    });

    effect(() => {
      const matchId = this.matchId();
      const hasReducedMatch = this.session.matches().has(matchId);

      if (
        matchId > 0
        && !this.session.matchesLoading()
        && hasReducedMatch
        && !this.matchData.hasMatchDetails(matchId)
        && !this.matchData.isMatchDetailsLoading(matchId)
        && !this.matchData.hasMatchDetailsFailed(matchId)
      ) {
        void this.matchData.loadMatchDetails(matchId);
      }
    });

    effect(() => {
      const detail = this.matchDetail();
      if (!detail) {
        return;
      }

      if (detail.tabs.length && !detail.tabs.some(tab => tab.id === this.activeTab())) {
        this.activeTab.set(detail.tabs[0].id);
      }
    });
  }

  goBack(): void {
    const state = this.location.getState() as { fromSportsbookHome?: boolean } | null;

    if (state?.fromSportsbookHome === true) {
      this.location.back();
      return;
    }

    void this.router.navigate(['/'], { replaceUrl: true });
  }

  setActiveTab(tabId: MatchDetailTabId): void {
    this.activeTab.set(tabId);
  }

  protected readonly trackTab = (_: number, tab: MatchDetailTabViewModel): string => tab.id;
  protected readonly trackSection = (_: number, section: MatchDetailSectionViewModel): string => section.id;
}
